<template>
  <div>
    <!-- Page header -->
    <div class="mb-4">
      <p class="text-2xl font-weight-bold mb-1">
        Saldo
      </p>
      <p class="text-xs text--secondary mb-0">
        Acompanhe seu saldo, tarifas e movimentos
      </p>
    </div>

    <!-- Balance + Tariffs row -->
    <v-row
      v-if="availableLimit !== null || dados.length"
      class="mb-2"
      align="stretch"
    >
      <v-col
        v-if="availableLimit !== null"
        cols="12"
        md="4"
        class="d-flex flex-column"
      >
        <div class="section-label mb-2">
          Saldo
        </div>
        <wallet-extract-balance-card
          :available-limit="availableLimit"
          class="flex-grow-1"
        />
      </v-col>
      <v-col
        v-if="dados.length"
        cols="12"
        md="8"
      >
        <div class="section-label mb-2">
          Tarifa dos Serviços
        </div>
        <wallet-extract-statistics-card :dados="dados" />
      </v-col>
    </v-row>

    <!-- Movements -->
    <div class="d-flex align-center mt-5 mb-3">
      <div>
        <p class="text-xl font-weight-bold mb-0">
          Movimentos
        </p>
        <p class="text-xs text--secondary mb-0">
          Histórico de débitos e recargas
        </p>
      </div>
      <v-spacer />
      <date
        v-model="filterYearMonth"
        :type="'month'"
        outlined
        hide-details
        dense
        label="Mês"
        readonly
        style="max-width: 200px;"
        @input="getExtract"
      />
    </div>

    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="movements"
        item-key="id"
        disable-sort
        class="movements-table"
      >
        <template #[`item.action`]="{ item }">
          <v-btn
            color="info"
            icon
            small
            title="Visualizar"
            @click="setExtractDetail(item)"
          >
            <v-icon size="18">
              {{ icons.mdiEye }}
            </v-icon>
          </v-btn>
        </template>

        <template #[`item.id`]="{ item }">
          <code class="txn-id">#{{ item.id }}</code>
        </template>

        <template #[`item.walletMovements[0].description`]="{ item }">
          <span
            v-if="item.walletMovements && item.walletMovements.length"
            class="description-cell"
          >
            {{ item.walletMovements[0].description }}
          </span>
          <span
            v-else
            class="text--secondary text-caption"
          >—</span>
        </template>

        <template #[`item.type`]="{ item }">
          <v-chip
            small
            :outlined="item.type === 'campaign'"
            :color="item.type === 'campaign' ? 'primary' : 'success'"
            class="type-chip"
          >
            <v-icon
              left
              size="13"
            >
              {{ item.type === 'campaign' ? icons.mdiBullhornOutline : icons.mdiCashPlus }}
            </v-icon>
            {{ item.type === 'campaign' ? 'Campanha' : 'Recarga' }}
          </v-chip>
        </template>

        <template #[`item.createdAt`]="{ item }">
          <div class="date-cell">
            <span>{{ $day(item.createdAt).format('DD/MM/YYYY') }}</span>
            <span class="text--secondary">{{ $day(item.createdAt).format('HH:mm') }}</span>
          </div>
        </template>

        <template #[`item.value`]="{ item }">
          <span
            :class="[resolveBalanceClass(item.value), 'value-cell']"
          >
            <span class="value-sign">{{ resolveBalanceSign(item.value) }}</span>
            R$ {{ $money(Math.abs(item.value), 2) }}
          </span>
        </template>

        <template #[`item.data-table-rows`]="{ item }">
          <tr :class="{ 'row-recharge': item.type !== 'campaign' }">
            <slot />
          </tr>
        </template>
      </v-data-table>
    </v-card>

    <!-- Movement detail dialog -->
    <dialog-form
      :status="dialogExtractDetail"
      :title="`Movimento #${extractDetail.id}`"
      :width="1000"
      :hide-actions="true"
      @close="dialogExtractDetail = !dialogExtractDetail"
    >
      <v-row dense>
        <v-col
          cols="12"
          md="3"
          sm="6"
        >
          <v-text-field
            :value="$day(extractDetail.createdAt).format('DD/MM/YYYY HH:mm:ss')"
            disabled
            outlined
            hide-details
            dense
            label="Data"
          />
        </v-col>
        <v-col
          cols="12"
          md="3"
          sm="6"
        >
          <v-text-field
            :value="$money(extractDetail.value, 2)"
            disabled
            outlined
            dense
            hide-details
            prefix="R$"
            label="Valor (R$)"
          />
        </v-col>
      </v-row>

      <v-row class="mt-3">
        <v-col cols="12">
          <v-data-table
            :headers="headersDetail"
            :items="extractDetail.walletMovements"
            item-key="id"
            disable-sort
            dense
            hide-default-footer
          >
            <template #[`item.createdAt`]="{ item }">
              {{ $day(item.createdAt).format('DD/MM/YYYY HH:mm:ss') }}
            </template>
            <template #[`item.value`]="{ item }">
              {{ $money(item.value, 2) }}
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </dialog-form>
  </div>
</template>

<script>
import {
  mdiEye,
  mdiFileDocument,
  mdiMessageTextOutline,
  mdiPhonePlusOutline,
  mdiBullhornOutline,
  mdiCashPlus,
} from '@mdi/js'
import { mapActions, mapState } from 'vuex'
import { ref } from 'vue'

import WalletExtractStatisticsCard from '@/components/wallet-extract-statistics-card/WalletExtractStatisticsCard.vue'
import WalletExtractBalanceCard from '@/components/wallet-extract-balance-card/WalletExtractBalanceCard.vue'

export default {
  components: {
    WalletExtractStatisticsCard,
    WalletExtractBalanceCard,
  },
  computed: {
    ...mapState('customerTaxes', ['customTaxes']),
    dados() {
      return this.customTaxes.map(item => {
        const service = this.services.find(i => i.id === item.serviceId)

        return {
          title: service ? service.name : '',
          total: item.unitaryValue,
        }
      })
    },
  },
  setup() {
    const filterYearMonth = ref(null)
    const extractDetail = ref({})
    const dialogExtractDetail = ref(false)
    const total = ref([])
    const movements = ref([])
    const services = ref([])
    const availableLimit = ref(null)

    const resolveBalanceClass = value => (value <= 0 ? 'error--text' : 'success--text')
    const resolveBalanceSign = value => (value <= 0 ? '−' : '+')

    return {
      filterYearMonth,
      extractDetail,
      dialogExtractDetail,
      services,
      resolveBalanceClass,
      resolveBalanceSign,
      icons: {
        mdiEye,
        mdiFileDocument,
        mdiMessageTextOutline,
        mdiPhonePlusOutline,
        mdiBullhornOutline,
        mdiCashPlus,
      },
      headers: [
        {
          text: 'Ações', value: 'action', align: 'center', width: '70px',
        },
        { text: 'Transação', value: 'id', width: '120px' },
        { text: 'Descrição', value: 'walletMovements[0].description' },
        { text: 'Serviço', value: 'type', width: '130px' },
        { text: 'Data', value: 'createdAt', width: '170px' },
        {
          text: 'Valor (R$)', value: 'value', align: 'right', width: '130px',
        },
      ],
      headersDetail: [
        { text: 'Código', value: 'id' },
        { text: 'Data', value: 'createdAt' },
        { text: 'Descrição', value: 'description' },
        {
          text: 'Valor (R$)', value: 'value', align: 'right',
        },
      ],
      movements,
      total,
      availableLimit,
    }
  },

  async created() {
    await this.findServices()
    await this.getExtract()
    await this.findCustomerTaxes({ userId: this.filterUser })
  },

  methods: {
    ...mapActions('customerTaxes', {
      findCustomerTaxes: 'findCustomTaxes',
    }),
    ...mapActions('services', {
      fetchServices: 'find',
    }),
    ...mapActions('walletExtract', {
      fechWalletExtract: 'find',
      createMovement: 'create',
    }),

    setExtractDetail(item) {
      this.extractDetail = item
      this.dialogExtractDetail = true
    },

    async findServices() {
      this.services = (await this.fetchServices()).map(service => ({
        ...service,
        total: '0 envio',
      }))
    },

    async getExtract() {
      const { total, wallets } = await this.fechWalletExtract({
        yearMonth: this.filterYearMonth
          ? this.$day(this.filterYearMonth, 'MM/YYYY').format('YYYY-MM')
          : undefined,
      })

      this.availableLimit = total.availableLimit ?? 0
      this.movements = wallets
    },
  },
}
</script>

<style lang="scss" scoped>
.section-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  opacity: 0.5;
}

.txn-id {
  font-family: 'Fira Mono', 'Courier New', monospace;
  font-size: 0.78rem;
  background: transparent;
  color: inherit;
  opacity: 0.6;
  padding: 0;
}

.description-cell {
  font-size: 0.875rem;
  font-weight: 500;
}

.date-cell {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
  font-size: 0.8rem;

  span:first-child {
    font-weight: 500;
  }

  span:last-child {
    font-size: 0.72rem;
    opacity: 0.55;
  }
}

.value-cell {
  font-size: 0.88rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
}

.value-sign {
  font-size: 0.95rem;
  line-height: 1;
  margin-right: 1px;
}

.type-chip {
  border-radius: 4px !important;

  ::v-deep .v-chip__content {
    font-size: 0.72rem;
    letter-spacing: 0.02em;
  }
}

::v-deep .movements-table {
  .v-data-table__wrapper tbody tr {
    transition: background 0.15s ease;

    &:hover {
      background: rgba(6, 241, 26, 0.04) !important;
    }
  }

  .v-data-table__wrapper tbody tr.row-recharge {
    border-left: 2px solid rgba(0, 200, 83, 0.5);
  }
}
</style>
